import { confirmQuestion } from 'components/v2/confirm-dialog/confirmQuestion';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { Button } from 'primereact/button';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUERY_KEYS } from 'services/api/constants';
import { autofillLcaResultsNd } from 'services/api/epdLcaResultsApi';
import styled from 'styled-components';
import { FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import queryClient from 'util/queryClient';
import { PoorMansError } from 'util/utils';
import { useNotifyLcaImported } from '../../grids/lca-results/util/LcaImportContext';

interface Props {
  epdVersionId: string;
  hasSomeResultsAlready: boolean;
}

const LcaResultsAutofillNd: React.FC<Props> = ({ epdVersionId, hasSomeResultsAlready }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const notifyImported = useNotifyLcaImported();

  const onAutofillNd = () => {
    setIsLoading(true);
    const performAutofill = () => {
      autofillLcaResultsNd(epdVersionId)
        .then(async () => {
          setIsLoading(false);
          await Promise.allSettled([
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_LCA_RESULTS] }),
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_ACTIONS_STATES] }),
          ]);
          notifyImported();
        })
        .catch((exception) => {
          setIsLoading(false);
          const errors = PoorMansError(exception);
          toaster({
            severity: 'error',
            summary: 'Error',
            details: errors ? errors : 'Error autofilling ND',
          });
        });
    };

    if (!hasSomeResultsAlready) {
      performAutofill();
    } else {
      confirmQuestion({
        caption: 'Are you sure you want to autofill "ND" values?',
        description: (
          <div>
            Clicking "Autofill ND" fills relevant cells in mandatory Results tables with "ND" based on modules marked as "ND"
            in the "Declared modules" table ("System boundary" tab).
            <br />
            Any existing data will be replaced.
          </div>
        ),
        acceptLabel: t('Submit'),
        rejectLabel: t('Cancel'),
        onConfirm: performAutofill,
        onReject: () => setIsLoading(false),
        closable: false,
        contentStyle: { paddingTop: '2.5rem' },
      });
    }
  };

  return (
    <FieldPanelFullWidth style={{ gridRowGap: 16 }}>
      <HelpBox style={{ flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <span>
          Non-declared LCA results can be automatically filled with "ND" for all mandatory indicators based on selections in
          the "Declared modules" table ("System boundary" tab).
          <br />
          If existing data is present, clicking the "Autofill ND" button will replace it with "ND".
        </span>
        <StyledValidateErrorsButton onClick={onAutofillNd} disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Autofill ND'}
        </StyledValidateErrorsButton>
      </HelpBox>
    </FieldPanelFullWidth>
  );
};

const StyledValidateErrorsButton = styled(Button)`
  background-color: '#CC302B';
  border-color: '#CC302B';
  min-width: 120px;
  border-radius: 4px;
  font-size: 1rem;
  padding: 0.5rem 1rem;

  &:disabled {
    background-color: ${(props) => props.theme.colors.elementsButtonDisabled};
    border-color: ${(props) => props.theme.colors.elementsButtonDisabled};
  }

  height: 1.5rem;
  min-height: 22px;
  ${(props) => props.theme.typography.buttonButtonDefault};
`;

export default LcaResultsAutofillNd;
